import { useEffect, useMemo, useState } from "react";
import { PageHeader, EmptyState, Pill } from "@/components/ui-bits";
import { Plus, Trash2, Pencil, Users, X, Search, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Batches() {
  const [items, setItems] = useState([]);
  const [courses, setCourses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ batch_name: "", course_id: "", track: "" });
  const [editing, setEditing] = useState(null);           // batch object being edited
  const [managing, setManaging] = useState(null);         // batch object whose roster we're editing

  const load = () => {
    api.get("/batches").then(r => setItems(r.data));
    api.get("/courses").then(r => setCourses(r.data));
  };
  useEffect(load, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.batch_name.trim() || !form.course_id) return;
    try {
      await api.post("/batches", {
        batch_name: form.batch_name.trim(),
        course_id: form.course_id,
        track: form.track || null,
      });
      toast.success("Batch created");
      setForm({ batch_name: "", course_id: "", track: "" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create batch");
    }
  };

  const del = async (b) => {
    if (!window.confirm(`Delete batch “${b.batch_name}”? Coders assigned to it will be un-assigned.`)) return;
    await api.delete(`/batches/${b.id}`);
    toast.success("Batch deleted");
    load();
  };

  const saveEdit = async (patch) => {
    await api.patch(`/batches/${editing.id}`, patch);
    toast.success("Batch updated");
    setEditing(null);
    load();
  };

  return (
    <div>
      <PageHeader
        title="Batches"
        subtitle="Create custom batches and assign coders (employees) to each batch."
        actions={
          <button
            data-testid="batch-add-button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 bg-blue-600 text-white rounded-lg px-4 py-2 font-medium hover:bg-blue-700">
            <Plus className="w-4 h-4" /> New batch
          </button>
        }
      />

      {open && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
          <form onSubmit={create} className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Batch name</label>
              <input required data-testid="batch-name-input"
                value={form.batch_name}
                onChange={e => setForm({ ...form, batch_name: e.target.value })}
                placeholder="e.g. Trainee - Feb 2026"
                className={input} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Course</label>
              <select required value={form.course_id}
                onChange={e => setForm({ ...form, course_id: e.target.value })}
                className={input} data-testid="batch-course-select">
                <option value="">— Select course —</option>
                {courses.map(c => <option key={c.id} value={c.id}>{c.course_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Track</label>
              <select value={form.track}
                onChange={e => setForm({ ...form, track: e.target.value })}
                className={input} data-testid="batch-track-select">
                <option value="">— None —</option>
                <option value="Trainee">Trainee</option>
                <option value="Lateral Coders">Lateral Coders</option>
              </select>
            </div>
            <div className="md:col-span-3 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg text-sm">Cancel</button>
              <button data-testid="batch-create-submit" type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm">Create batch</button>
            </div>
          </form>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState title="No batches yet" hint="Create your first custom batch and assign coders." />
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-6 py-3 font-medium">Batch</th>
                <th className="px-6 py-3 font-medium">Track</th>
                <th className="px-6 py-3 font-medium">Course</th>
                <th className="px-6 py-3 font-medium">Coders</th>
                <th className="px-6 py-3 font-medium">Created</th>
                <th className="px-6 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(b => {
                const course = courses.find(c => c.id === b.course_id);
                return (
                  <tr key={b.id} className="border-t border-slate-100" data-testid={`batch-row-${b.id}`}>
                    <td className="px-6 py-3 font-medium text-slate-900">{b.batch_name}</td>
                    <td className="px-6 py-3">
                      {b.track
                        ? <Pill tone={b.track === "Trainee" ? "indigo" : "amber"}>{b.track}</Pill>
                        : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-6 py-3 text-slate-600">{course?.course_name || "—"}</td>
                    <td className="px-6 py-3">
                      <Pill tone={b.coder_count > 0 ? "blue" : "slate"}>
                        {b.coder_count || 0} coder{b.coder_count === 1 ? "" : "s"}
                      </Pill>
                    </td>
                    <td className="px-6 py-3 text-slate-500">{b.created_at?.slice(0, 10)}</td>
                    <td className="px-6 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`batch-manage-coders-${b.id}`} onClick={() => setManaging(b)}
                          title="Manage coders"
                          className="inline-flex items-center gap-1.5 text-xs font-medium border border-slate-300 hover:border-blue-400 hover:bg-blue-50 text-slate-700 rounded-lg px-2.5 py-1.5">
                          <Users className="w-3.5 h-3.5" /> Manage coders
                        </button>
                        <button data-testid={`batch-edit-${b.id}`} onClick={() => setEditing(b)}
                          title="Edit batch details"
                          className="text-slate-600 hover:bg-slate-100 rounded-lg p-2">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button data-testid={`batch-delete-${b.id}`} onClick={() => del(b)}
                          title="Delete batch"
                          className="text-red-600 hover:bg-red-50 rounded-lg p-2">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditBatchModal
          batch={editing}
          courses={courses}
          onSave={saveEdit}
          onClose={() => setEditing(null)}
        />
      )}

      {managing && (
        <ManageCodersModal
          batch={managing}
          onClose={() => setManaging(null)}
          onSaved={() => { setManaging(null); load(); }}
        />
      )}
    </div>
  );
}

function EditBatchModal({ batch, courses, onSave, onClose }) {
  const [name, setName] = useState(batch.batch_name || "");
  const [courseId, setCourseId] = useState(batch.course_id || "");
  const [track, setTrack] = useState(batch.track || "");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !courseId) return;
    setSaving(true);
    try {
      await onSave({ batch_name: name.trim(), course_id: courseId, track: track || null });
    } finally { setSaving(false); }
  };
  return (
    <div data-testid="edit-batch-modal" onClick={onClose}
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-center px-4">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900">Edit batch</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Batch name</label>
            <input required value={name} onChange={e => setName(e.target.value)}
              data-testid="edit-batch-name" className={input} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Course</label>
            <select value={courseId} onChange={e => setCourseId(e.target.value)}
              className={input} data-testid="edit-batch-course">
              {courses.map(c => <option key={c.id} value={c.id}>{c.course_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Track</label>
            <select value={track} onChange={e => setTrack(e.target.value)}
              className={input} data-testid="edit-batch-track">
              <option value="">— None —</option>
              <option value="Trainee">Trainee</option>
              <option value="Lateral Coders">Lateral Coders</option>
            </select>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-300 rounded-lg text-sm">Cancel</button>
          <button data-testid="edit-batch-save" type="submit" disabled={saving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ManageCodersModal({ batch, onClose, onSaved }) {
  const [allStudents, setAllStudents] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("all"); // all | in-batch | unassigned

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/users?role=student"),
      api.get(`/batches/${batch.id}/coders`),
    ]).then(([su, mem]) => {
      setAllStudents(su.data);
      setPicked(new Set(mem.data.map((u) => u.id)));
    }).finally(() => setLoading(false));
  }, [batch]);

  const list = useMemo(() => {
    const s = q.toLowerCase();
    return allStudents.filter((u) => {
      if (mode === "in-batch" && u.batch_id !== batch.id) return false;
      if (mode === "unassigned" && u.batch_id && u.batch_id !== batch.id) return false;
      if (!s) return true;
      return (u.name || "").toLowerCase().includes(s)
        || (u.email || "").toLowerCase().includes(s)
        || (u.employee_id || "").toLowerCase().includes(s);
    });
  }, [allStudents, q, mode, batch]);

  const toggle = (id) => {
    const s = new Set(picked);
    s.has(id) ? s.delete(id) : s.add(id);
    setPicked(s);
  };
  const selectAllVisible = () => setPicked(new Set([...picked, ...list.map((u) => u.id)]));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/batches/${batch.id}/coders`, { user_ids: Array.from(picked) });
      toast.success(`Batch “${batch.batch_name}”: ${data.assigned} coder${data.assigned === 1 ? "" : "s"} assigned`);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save coders");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="manage-coders-modal" onClick={onClose}
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center px-4 py-6">
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        <div className="p-5 border-b border-slate-100 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 grid place-items-center flex-shrink-0">
            <Users className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-slate-900 truncate">Coders in “{batch.batch_name}”</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Assign employees to this batch. Ticked users will be moved here from any previous batch.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-3 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input placeholder="Search name / email / EMP ID"
              value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="coder-search"
              className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
          </div>
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {[
              { id: "all", label: "All" },
              { id: "in-batch", label: "In this batch" },
              { id: "unassigned", label: "Unassigned" },
            ].map((m) => (
              <button key={m.id} type="button" onClick={() => setMode(m.id)}
                data-testid={`coder-mode-${m.id}`}
                className={`px-2.5 py-1.5 rounded-md text-xs font-medium ${mode === m.id ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:text-slate-900"}`}>
                {m.label}
              </button>
            ))}
          </div>
          <button type="button" onClick={selectAllVisible}
            disabled={list.length === 0}
            data-testid="coder-select-all"
            className="text-xs font-medium text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100 rounded-lg px-3 py-2 disabled:opacity-50">
            Select all visible
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pb-4 min-h-0">
          {loading ? (
            <div className="text-center text-sm text-slate-500 py-10">Loading…</div>
          ) : list.length === 0 ? (
            <div className="text-center text-sm text-slate-500 py-10">No matching employees.</div>
          ) : (
            <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100">
              {list.map((u) => {
                const on = picked.has(u.id);
                return (
                  <li key={u.id}>
                    <button type="button" onClick={() => toggle(u.id)}
                      data-testid={`coder-toggle-${u.id}`}
                      className={`w-full text-left flex items-center gap-3 px-4 py-2.5 transition ${on ? "bg-blue-50" : "hover:bg-slate-50"}`}>
                      <div className={`w-5 h-5 rounded border flex-shrink-0 grid place-items-center ${on ? "bg-blue-600 border-blue-600" : "border-slate-300 bg-white"}`}>
                        {on && <Check className="w-3.5 h-3.5 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{u.name}</div>
                        <div className="text-xs text-slate-500 truncate">
                          {u.employee_id && <span className="font-mono-acc mr-2">{u.employee_id}</span>}
                          {u.email}
                          {u.batch_id && u.batch_id !== batch.id && (
                            <span className="ml-2 text-amber-600">· currently in another batch</span>
                          )}
                          {u.batch_id === batch.id && (
                            <span className="ml-2 text-emerald-600">· in this batch</span>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="border-t border-slate-100 px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm text-slate-600" data-testid="coder-count">
            <b className="text-slate-900">{picked.size}</b> employee{picked.size === 1 ? "" : "s"} will be assigned to this batch
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} disabled={saving}
              className="px-4 py-2 border border-slate-300 hover:bg-slate-50 rounded-lg text-sm font-medium">
              Cancel
            </button>
            <button data-testid="coder-save" onClick={save} disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
              <Users className="w-4 h-4" /> {saving ? "Saving…" : "Save assignments"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const input = "w-full border border-slate-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-slate-900 bg-white";
